import React, { useMemo, useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const k_config = {
    marginTop: 20,
    marginRight: 20,
    marginBottom: 50,
    marginLeft: 120
}

interface ChartConfig{
    minDate: number
    maxDate: number
    maxX: number // 像素值，x轴宽度
    maxY: number // 像素值，Y轴高度
}
const XAxis: React.FC<{ scale?: d3.ScaleTime<number, number>, chart?: ChartConfig }> = ({ scale, chart }) => {
    const axisRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (axisRef.current && scale) {
            
            const xAxis = d3.axisBottom(scale)
                // .ticks(d3.timeDay.every(1))
                .tickFormat((d) => d3.timeFormat("%Y-%m")(d as Date)); // 格式化刻度为 YYYY-MM
            
            const svg = d3.select(axisRef.current)
            svg.selectAll("*").remove()
            svg.call(xAxis);
        }
    }, [scale, chart]);

    return <g ref={axisRef} transform={`translate(0, ${k_config.marginTop})`} className="text-dark-bg" />;
};
interface IXSlider{
    scale?: d3.ScaleTime<number, number>
    chart?: ChartConfig
    range: number[] | null
    onChange: (val: number[]) => void
}
const XSlider: React.FC<IXSlider> = ({ scale, chart, onChange, range }) => {
    const gRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (!gRef.current || !chart || !scale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()

        let minX = chart.minDate
        let maxX = chart.maxDate
        if (range && range.length == 2) {
            minX = Math.min(...range)
            maxX = Math.max(...range)
        }

        // 圆点初始位置
        const points = [{ index: 0, date: minX }, { index: 1, date: maxX }];

        // 连接线
        const line = svg.append("line")
            .attr("class", "connector")
            .attr("stroke", "#1f1547")
            .attr("stroke-width", 4);

        svg.selectAll(".dot")
            .data(points)
            .enter()
            .append("polygon") // 改为polygon元素来绘制三角形
            .attr("class", "dot")
            .attr("points", "-10,15 0,0 10,15") // 三角形的三个顶点坐标
            .attr("transform", d => `translate(${scale(d.date)}, 20)`) // 使用transform来定位
            .attr("fill", "#1f1547")
            .attr("cursor", "move")
            .call(
                d3.drag<SVGPolygonElement, { date: number, index: number }>()
                    .on("drag", function (event: d3.D3DragEvent<SVGPolygonElement, { date: number, index: number }, unknown>, d){
                        const date = scale.invert(event.x)
                        const x = scale(date)
                        if (x < k_config.marginLeft || x > chart.maxX) return
                        d3.select(this)
                            .attr("transform", `translate(${x}, 20)`); // 更新三角形位置
                        
                        let x0, x1
                        if (d.index === 0) {
                            x0 = date.getTime()
                            x1 = points[1].date
                            // 更新points数组中的数据
                            points[0].date = x0
                        } else {
                            x1 = date.getTime()
                            x0 = points[0].date
                            // 更新points数组中的数据
                            points[1].date = x1
                        }
                        
                        onChange([x0, x1])
                        updateConnector()
                    })
            );

        function updateConnector() {
            if(!scale) return
            line.attr("x1", scale(points[0].date))
                .attr("y1", k_config.marginTop)
                .attr("x2", scale(points[1].date))
                .attr("y2", k_config.marginTop);
        }
        updateConnector()

    }, [scale, chart, range])
    return <g ref={gRef}  />;
};
interface IProps{
    config: {
        minDate: number
        maxDate: number
    }
    value: number[] | null
    onChange: (v: number[])=>void
}
const AxisIndex: React.FC<IProps> = (props) => {
    const [svgWidth, setSvgWidth] = useState(500)
    const svgBoxRef = useRef<HTMLDivElement>(null)
    const isFreezeRange = useRef(false)
    const xAllScale = useMemo(() => {
        if (!props.config) return
        const { minDate, maxDate } = props.config
            return d3.scaleTime() // 使用band比例尺来处理离散的字符串数据
                .domain([minDate, maxDate]) // 定义坐标轴的字符串值
                .range([k_config.marginLeft, svgWidth - k_config.marginRight]) // SVG中的像素范围
    }, [svgWidth, props.config])

    const config = useMemo(() => {
        const { minDate, maxDate } = props.config
        return {
            minDate,
            maxDate,
            maxX: svgWidth - k_config.marginRight,
            maxY: 40
        }
    }, [svgWidth, props.config])

    useEffect(() => {
        if(!props.config) return
        if (!isFreezeRange.current && props.value) {
            props.onChange(props.value)
        }
    }, [svgWidth, props.config])
    
    const onRangeChange = (v: number[]) => {
        isFreezeRange.current = true;
        props.onChange(v)
    }
    useEffect(() => {
        const resize = () => {
            if (!svgBoxRef.current) return
            setSvgWidth(svgBoxRef.current.clientWidth || 500)
        }

        resize()
        window.addEventListener("resize", resize)
        return () => {
            window.removeEventListener("resize", resize)
        }
    }, [])
    return (
        <div className="shrink-0" ref={svgBoxRef}>
            <svg className="w-full" height={50}>
                <XAxis scale={xAllScale} chart={config} />
                <XSlider scale={xAllScale} chart={config} range={props.value}
                    onChange={onRangeChange} />
            </svg>
        </div> 
    )
}
export default AxisIndex